// ===========================================================================
// Subida de imagenes de producto
// ---------------------------------------------------------------------------
// El binario nunca pasa por Supabase: va del navegador a R2 en un PUT firmado.
// Tres pasos, y el tercero es el que manda.
//
//   1. storage-presign  -> permiso, clave y URL firmada de 5 minutos
//   2. PUT a R2         -> el archivo, directo
//   3. storage-confirm  -> R2 confirma que el objeto existe; recien ahi se
//                          escribe la fila
//
// Si el navegador se cierra entre el 2 y el 3, queda un objeto sin fila. Es
// el fallo barato. Al reves —fila sin objeto— seria una imagen rota en la
// tienda, y por eso el orden no se invierte.
// ===========================================================================

import { supabase } from "@/lib/supabase";

const MAX_BYTES = 5 * 1024 * 1024;

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/avif"];

export interface UploadedImage {
  id: string;
  storage_key: string;
  is_primary: boolean;
  sort_order: number;
}

/**
 * Las Edge Functions devuelven `{ error: CODIGO }` con un estado HTTP. El
 * cliente de Supabase solo expone "non-2xx status", asi que hay que leer el
 * cuerpo para recuperar el codigo y poder traducirlo.
 */
async function callFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, { body });

  if (error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      try {
        const payload = await context.clone().json();
        if (payload?.error) throw new Error(payload.error);
      } catch (parsed) {
        if (parsed instanceof Error && parsed.message) throw parsed;
      }
      if (context.status === 404) throw new Error("STORAGE_NOT_CONFIGURED");
    }
    throw error;
  }

  return data as T;
}

/**
 * El alto y el ancho se leen aqui, no en el servidor: el binario no pasa por
 * ningun backend nuestro. Se guardan para reservar el hueco en el layout de
 * la tienda y evitar el salto cuando la foto termina de cargar.
 */
async function readDimensions(
  file: File,
): Promise<{ width: number | null; height: number | null }> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return { width: null, height: null };
  }
}

export function validateImageFile(file: File): string | null {
  if (!ALLOWED.includes(file.type)) return "UNSUPPORTED_FILE_TYPE";
  if (file.size > MAX_BYTES) return "FILE_TOO_LARGE";
  if (file.size === 0) return "INVALID_FILE_SIZE";
  return null;
}

export async function uploadProductImage(
  organizationId: string,
  productId: string,
  file: File,
): Promise<UploadedImage> {
  const invalid = validateImageFile(file);
  if (invalid) throw new Error(invalid);

  const presign = await callFunction<{
    upload_url: string;
    storage_key: string;
  }>("storage-presign", {
    organization_id: organizationId,
    purpose: "product",
    entity_id: productId,
    content_type: file.type,
    content_length: file.size,
  });

  // La firma incluye content-type y content-length: R2 rechaza el PUT si el
  // cuerpo no coincide exactamente con lo que se declaro al firmar.
  const put = await fetch(presign.upload_url, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });

  if (!put.ok) throw new Error("UPLOAD_FAILED");

  const { width, height } = await readDimensions(file);

  const { image } = await callFunction<{ image: UploadedImage }>(
    "storage-confirm",
    {
      organization_id: organizationId,
      purpose: "product",
      entity_id: productId,
      storage_key: presign.storage_key,
      original_filename: file.name,
      width,
      height,
    },
  );

  return image;
}

export async function deleteProductImage(
  organizationId: string,
  imageId: string,
): Promise<void> {
  await callFunction("storage-delete", {
    organization_id: organizationId,
    image_id: imageId,
  });
}

/**
 * Cambiar la portada no toca R2, solo dos filas: se puede hacer directo
 * contra la base, donde RLS vuelve a comprobar organizacion y rol.
 */
export async function setPrimaryImage(
  productId: string,
  imageId: string,
): Promise<void> {
  const { error: clearError } = await supabase
    .from("product_images")
    .update({ is_primary: false })
    .eq("product_id", productId)
    .is("deleted_at", null);

  if (clearError) throw clearError;

  const { error } = await supabase
    .from("product_images")
    .update({ is_primary: true })
    .eq("id", imageId);

  if (error) throw error;
}
